import { useState } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Flex, Grid, Typography, theme } from 'antd';
import { Logotipo } from '@titularis/ui';
import type { NewsListItem } from '@titularis/api-client';
import { api } from '../api';
import { dataDaNoticia } from '../noticias';
import { enderecoDaNoticia, enderecoDasNoticias } from '../site';
import './noticias-recentes.css';

/**
 * As Notícias Recentes na tela de início.
 *
 * **Três fileiras, e não uma vitrine.** A tela de início é um bloco só, com uma
 * saudação e uma ação - a forma que o Calebe pediu, a da tela inicial do Claude.
 * O que o Claude põe embaixo do campo de texto são os *recentes*: fileiras
 * baixas, texto pequeno, sem moldura. É a mesma peça aqui, e é o que permite
 * acrescentar conteúdo sem transformar a entrada numa parede de cartões - que é
 * exatamente o que esta tela deixou de ser em 05/09/2026.
 *
 * **A capa é o que faz a fileira valer.** Notícia sem imagem numa lista de
 * títulos parece rodapé de site; com a capa, cada linha vira uma coisa que se
 * reconhece de relance - o cartaz do CNJ, a capa do provimento. Ela tem tamanho
 * fixo e a fileira tem altura fixa por causa disso: capa de proporção livre
 * (as publicadas vão de 533x308 a 936x1061) empurraria cada linha para uma
 * altura diferente, e três alturas diferentes numa coluna de 520px é o que
 * quebra o alinhamento com o cartão da ação principal logo acima.
 *
 * **Por que a notícia abre em outra aba:** ela mora no site público, que já tem
 * a página pronta, indexável e com prévia de link (ver `site.ts`). Levar o
 * aluno para lá na mesma aba seria tirá-lo do estudo para mostrar uma notícia.
 */

/** Três: o que cabe na coluna sem empurrar a saudação para fora da dobra. */
const QUANTAS = 3;

/**
 * A capa: 96x64, e a altura é a que manda.
 *
 * **64 é a altura de um título de duas linhas com a data embaixo** - 42 + 2 +
 * 18,4, medidos no navegador com os tokens do tema (o corpo do texto é 15px
 * aqui, não 14). Com a capa mais baixa que isso, um título comprido passava a
 * mandar na altura da fileira, e a fileira de título curto ficava 2,4px mais
 * baixa que a vizinha - o bastante para a coluna de capas perder o prumo e para
 * a altura reservada da seção não fechar mais com a altura real.
 *
 * A largura encolhe no celular e a altura não: assim o título ganha os 20px que
 * faltavam numa tela de 390, e a fileira continua tendo a mesma altura em
 * qualquer tela - que é o que a reserva depende para ser exata.
 */
const ALTURA_DA_CAPA = 64;
const RECUO_DA_FILEIRA = 8;
const VAO_ENTRE_FILEIRAS = 4;
/** O cabeçalho da seção: o rótulo, o link e o respiro até a primeira fileira. */
const ALTURA_DO_CABECALHO = 30;

/**
 * A altura que a seção ocupa, calculada e não medida.
 *
 * Ela existe para o espaço estar reservado **antes** de a resposta chegar. Sem
 * isso a saudação, que é centralizada na vertical, nasce no meio da tela e
 * salta para cima quando as notícias carregam - o pulo é de meia seção e
 * acontece na primeira coisa que a pessoa olha ao abrir o app.
 */
const ALTURA_DA_SECAO =
  ALTURA_DO_CABECALHO +
  QUANTAS * (ALTURA_DA_CAPA + 2 * RECUO_DA_FILEIRA) +
  (QUANTAS - 1) * VAO_ENTRE_FILEIRAS;

export function NoticiasRecentes() {
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();

  const noticias = useQuery({
    queryKey: ['news', QUANTAS],
    queryFn: () => api.listNews(1, QUANTAS),
    // O editorial não muda de minuto em minuto, e a Home é a tela mais revisitada.
    staleTime: 10 * 60 * 1000,
  });

  // Esperando: o espaço reservado, e nada dentro. Um esqueleto cinza piscando
  // embaixo de "Boa noite, Calebe" cobra atenção que a seção não merece.
  if (noticias.isPending) return <div style={{ height: ALTURA_DA_SECAO }} aria-hidden />;

  /*
   * Sem notícia, ou com a consulta falhando, a seção **some**. Não há aviso: a
   * tela de início é a que responde "o que eu faço agora?", e um alerta sobre
   * uma seção secundária que não carregou responde outra pergunta.
   */
  const itens = noticias.data?.items ?? [];
  if (noticias.isError || itens.length === 0) return null;

  return (
    /*
     * A seção **sangra o próprio recuo para fora da coluna**, e é o que faz a
     * capa começar exatamente na borda do cartão da ação principal.
     *
     * Sem isto, o realce do ponteiro precisa de um respiro em volta do texto, e
     * esse respiro empurra a capa 8px para dentro: medido no navegador, o
     * cartão ia de 616 a 1136 e a fileira, de 624 a 1128. Oito pixels não se
     * enxergam sozinhos, mas duas verticais que quase coincidem é o que faz uma
     * seção parecer colada na outra em vez de fazer parte dela.
     */
    <section
      className="titularis-noticias"
      style={{ marginInline: -RECUO_DA_FILEIRA, textAlign: 'start' }}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        style={{ height: ALTURA_DO_CABECALHO, paddingInline: RECUO_DA_FILEIRA }}
      >
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Notícias recentes
        </Typography.Text>
        <Typography.Link
          href={enderecoDasNoticias}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: token.fontSizeSM }}
        >
          Ver todas <ExportOutlined />
        </Typography.Link>
      </Flex>

      <Flex vertical gap={VAO_ENTRE_FILEIRAS}>
        {itens.map((noticia) => (
          <Fileira key={noticia.slug} noticia={noticia} larguraDaCapa={telas.sm ? 96 : 76} />
        ))}
      </Flex>
    </section>
  );
}

function Fileira({ noticia, larguraDaCapa }: { noticia: NewsListItem; larguraDaCapa: number }) {
  const { token } = theme.useToken();

  return (
    <a
      className="titularis-noticia"
      href={enderecoDaNoticia(noticia.slug)}
      target="_blank"
      rel="noreferrer"
      style={
        {
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: RECUO_DA_FILEIRA,
          borderRadius: token.borderRadiusLG,
          // Herdado: a fileira inteira é o link, e um título em azul de link
          // sobre a capa transformaria a seção calma num diretório.
          color: 'inherit',
          '--noticia-cor-do-realce': token.colorFillQuaternary,
        } as React.CSSProperties
      }
    >
      <Capa noticia={noticia} largura={larguraDaCapa} />

      <Flex vertical gap={2} style={{ minWidth: 0 }}>
        {/*
          Duas linhas no máximo. Título de notícia jurídica é longo ("Provimento
          240 do CNJ muda o registro de nascimento tardio") e a terceira linha
          estouraria a altura da capa, que é o que segura o alinhamento.
        */}
        <Typography.Paragraph
          strong
          ellipsis={{ rows: 2 }}
          style={{ margin: 0, fontSize: token.fontSize, lineHeight: 1.4 }}
        >
          {noticia.title}
        </Typography.Paragraph>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {dataDaNoticia(noticia.publishedAt)}
        </Typography.Text>
      </Flex>
    </a>
  );
}

/**
 * A capa, ou o lugar dela.
 *
 * Notícia sem imagem não deixa a fileira sem a caixa: sem ela o título começa
 * 96px à esquerda do título de cima, e a coluna deixa de ser uma coluna. O
 * símbolo da marca preenche o vazio sem fingir que é uma imagem.
 *
 * O `onError` cobre o caso que o `temImagem` não vê: a rota da capa é pública e
 * cacheada por cinco minutos, então uma imagem trocada ou apagada agora ainda é
 * pedida por quem tem a página aberta. Ícone quebrado numa tela que existe para
 * acolher é pior que capa nenhuma.
 */
function Capa({ noticia, largura }: { noticia: NewsListItem; largura: number }) {
  const { token } = theme.useToken();
  const [falhou, setFalhou] = useState(false);

  const moldura = {
    width: largura,
    height: ALTURA_DA_CAPA,
    flexShrink: 0,
    borderRadius: token.borderRadius,
    background: token.colorFillTertiary,
  } as const;

  if (!noticia.temImagem || falhou) {
    return (
      <Flex align="center" justify="center" style={{ ...moldura, opacity: 0.45 }}>
        <Logotipo apenasSimbolo altura={22} />
      </Flex>
    );
  }

  return (
    <img
      src={api.newsImageUrl(noticia.slug)}
      // Decorativa: o título vem ao lado, em texto. Descrevê-la faria o leitor
      // de tela anunciar a mesma notícia duas vezes.
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFalhou(true)}
      style={{ ...moldura, objectFit: 'cover', display: 'block' }}
    />
  );
}
